import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  ChevronDown,
  Command,
  HelpCircle,
  LogOut,
  Rows2,
  Rows3,
  Search,
  User,
} from "lucide-react";
import { Breadcrumbs } from "./Breadcrumbs";
import { TenantChip } from "./TenantChip.js";
import { CommandPalette } from "./CommandPalette.js";
import { useAuth } from "../../context/AuthContext.js";
import { useTheme } from "../../hooks/useTheme.ts";
import { useDashboardEventStream } from "../../hooks/useDashboardEventStream.ts";
import { toggleDensity } from "../../lib/density-settings.js";
import { getAdminJwt } from "../../lib/auth-storage.js";
import {
  fetchOperatorNotifications,
  markOperatorNotificationRead,
} from "../../lib/api.js";

/**
 * The single global application header. Owns all cross-page chrome:
 * breadcrumbs, command palette (search / Cmd-K), density toggle, tenant chip,
 * in-app notifications, live event hints (revocations / SLA / rate quota),
 * and the account menu. Page titles and page-level actions are rendered
 * separately by the per-page <TopBar /> strip (RFC-004 R5.6).
 */
export function ContentHeader() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { density, setDensity } = useTheme();
  const { revocationHint, slaBreachHint, rateQuotaHint } = useDashboardEventStream();

  const [commandOpen, setCommandOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifData, setNotifData] = useState<{
    items: Array<{ id: number; title: string; body: string; readAt: string | null }>;
    unread: number;
  } | null>(null);

  const loadNotifications = useCallback(async () => {
    if (!getAdminJwt()) {
      setNotifData(null);
      return;
    }
    setNotifLoading(true);
    try {
      const d = await fetchOperatorNotifications();
      setNotifData(d);
    } catch {
      setNotifData({ items: [], unread: 0 });
    } finally {
      setNotifLoading(false);
    }
  }, []);

  // Load on open, and poll every 5 minutes while signed in (UX-05).
  useEffect(() => {
    if (!notifOpen) return;
    void loadNotifications();
  }, [notifOpen, loadNotifications]);

  useEffect(() => {
    if (!getAdminJwt()) {
      setNotifData(null);
      return;
    }
    void loadNotifications();
    const t = window.setInterval(() => void loadNotifications(), 300_000);
    return () => window.clearInterval(t);
  }, [loadNotifications]);

  // Cmd/Ctrl+K toggles the command palette.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setCommandOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const handleSignOut = async () => {
    setUserMenuOpen(false);
    await logout();
    navigate("/login");
  };

  const initials = user?.email ? user.email.slice(0, 2).toUpperCase() : "?";
  const DensityIcon = density === "compact" ? Rows2 : Rows3;
  const unreadCount = notifData?.unread ?? 0;

  return (
    <>
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between gap-3 border-b border-card bg-card-translucent px-6 backdrop-blur-sm shadow-[0_1px_3px_rgba(16,24,40,0.08)]">
        {/* Left: Breadcrumbs */}
        <Breadcrumbs />

        {/* Right: global controls */}
        <div className="flex items-center gap-1.5">
          {/* Live event hints (revocation / SLA breach / rate quota) */}
          {revocationHint || slaBreachHint || rateQuotaHint ? (
            <span
              className="hidden max-w-[220px] truncate text-[10px] text-amber-600 dark:text-amber-400/90 md:inline"
              title={[revocationHint, slaBreachHint, rateQuotaHint].filter(Boolean).join(" · ")}
            >
              {revocationHint ?? slaBreachHint ?? rateQuotaHint}
            </span>
          ) : null}

          {/* Search / command palette */}
          <button
            type="button"
            onClick={() => setCommandOpen(true)}
            className="flex items-center gap-2 rounded-lg border border-card bg-app px-3 py-1.5 text-sm text-subtle hover:text-app hover:border-app transition-colors"
            aria-label="Open command palette"
          >
            <Search className="h-3.5 w-3.5" />
            <span className="hidden md:inline text-xs">Search</span>
            <kbd className="hidden md:flex items-center gap-0.5 rounded border border-card px-1.5 py-0.5 text-[10px] font-mono">
              <Command className="h-2.5 w-2.5" />K
            </kbd>
          </button>

          <TenantChip />

          {/* Density toggle */}
          <button
            type="button"
            title={`Density: ${density}`}
            aria-label={`Toggle density (currently ${density})`}
            onClick={() => setDensity(toggleDensity(density))}
            className="rounded-lg p-2 text-muted hover:bg-app-hover hover:text-app transition-colors"
          >
            <DensityIcon className="h-[18px] w-[18px]" aria-hidden />
          </button>

          {/* Help */}
          <button
            type="button"
            className="rounded-lg p-2 text-muted hover:bg-app-hover hover:text-app transition-colors"
            aria-label="Help"
          >
            <HelpCircle className="h-[18px] w-[18px]" />
          </button>

          {/* Notifications */}
          <div className="relative">
            <button
              type="button"
              aria-expanded={notifOpen}
              aria-haspopup="dialog"
              aria-label="Notifications"
              onClick={() => setNotifOpen((o) => !o)}
              className="relative rounded-lg p-2 text-muted hover:bg-app-hover hover:text-app transition-colors"
            >
              <Bell className="h-[18px] w-[18px]" aria-hidden />
              {unreadCount > 0 ? (
                <span className="absolute -right-0.5 -top-0.5 flex min-h-[16px] min-w-[16px] items-center justify-center rounded-full bg-confidence-low px-1 text-[10px] font-bold leading-none text-white">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              ) : null}
            </button>
            {notifOpen ? (
              <div
                className="absolute right-0 mt-2 w-80 max-h-80 overflow-y-auto rounded-lg border border-card bg-card p-3 text-left shadow-lg"
                role="dialog"
                aria-label="Notification list"
              >
                <p className="text-xs font-semibold text-app mb-2">In-app alerts</p>
                {notifLoading ? (
                  <p className="text-xs text-subtle">Loading…</p>
                ) : !notifData?.items.length ? (
                  <p className="text-xs text-subtle">No notifications yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {notifData.items.map((n) => (
                      <li key={n.id}>
                        <button
                          type="button"
                          className="w-full rounded-md border border-card bg-app-hover px-2 py-1.5 text-left text-xs"
                          onClick={() => {
                            if (!n.readAt)
                              void markOperatorNotificationRead(n.id).then(() =>
                                loadNotifications()
                              );
                          }}
                        >
                          <span className="font-medium text-app">{n.title}</span>
                          <span className="block text-muted mt-0.5">{n.body}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : null}
          </div>

          {/* Account menu */}
          <div className="relative ml-1">
            <button
              type="button"
              onClick={() => setUserMenuOpen((o) => !o)}
              className="flex items-center gap-1.5 rounded-lg p-1 hover:bg-app-hover transition-colors"
              aria-label="Account menu"
              aria-expanded={userMenuOpen}
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[color:var(--brand-500)]">
                {user ? (
                  <span className="text-white text-xs font-semibold">{initials}</span>
                ) : (
                  <User className="h-4 w-4 text-white" />
                )}
              </div>
              {user && <ChevronDown className="h-3 w-3 text-subtle" />}
            </button>

            {userMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setUserMenuOpen(false)}
                />
                <div className="absolute right-0 top-full mt-1.5 z-20 w-56 overflow-hidden rounded-xl border border-card bg-card py-1 shadow-lg">
                  {user && (
                    <div className="border-b border-card px-4 py-2.5">
                      <p className="truncate text-xs font-semibold text-app">
                        {user.email}
                      </p>
                      <p className="mt-0.5 text-[11px] capitalize text-subtle">
                        {user.role?.replace("_", " ")}
                      </p>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => void handleSignOut()}
                    className="flex w-full items-center gap-2.5 px-4 py-2 text-sm text-muted hover:bg-app-hover hover:text-app transition-colors"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      {commandOpen && <CommandPalette onClose={() => setCommandOpen(false)} />}
    </>
  );
}
